'use client';
import { Grid, keyframes, Skeleton } from '@mui/material';
import { LOADING_STATUS_PROPS, LoadingStatusText, MOTION, SafeSpacePadder } from '@repo/ui';
import { useEffect, useState } from 'react';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

// When this skeleton last mounted and unmounted, so a skeleton replacing another one (route loading, then step
// initialization) keeps showing instead of blinking out and fading back in
let lastMountedAt = 0;
let lastUnmountedAt = 0;
const HANDOFF_WINDOW_MS = 100;

const getEntrance = () => {
  if (typeof window === 'undefined') return 'delayed';
  const now = performance.now();
  if (now - lastUnmountedAt > HANDOFF_WINDOW_MS) return 'delayed';
  // The previous skeleton had already faded in: carry on without animating
  return lastUnmountedAt - lastMountedAt > MOTION.fast ? 'none' : 'fade';
};

export default function Loading() {
  // Quick loads skip the skeleton entirely: it only fades in once loading takes longer than MOTION.fast
  const [entrance] = useState(getEntrance);

  useEffect(() => {
    lastMountedAt = performance.now();
    return () => {
      lastUnmountedAt = performance.now();
    };
  }, []);

  return (
    // Same boxes as a step (StepWrapper > OnbForm), so swapping the skeleton for the step barely moves anything
    <div style={{ display: 'flex', width: '100%', height: '100%' }}>
      <SafeSpacePadder
        sx={{
          animation:
            entrance === 'none'
              ? 'none'
              : `${fadeIn} ${MOTION.short}ms ${MOTION.easeOut} ${entrance === 'delayed' ? MOTION.fast : 0}ms both`,
        }}
      >
        <Grid
          {...LOADING_STATUS_PROPS}
          container
          sx={{ flexDirection: 'column', flexWrap: 'nowrap', height: '100%', gap: 2 }}
        >
          <LoadingStatusText />
          {/* Back arrow: a 44px touch target on phones */}
          <Grid sx={{ display: 'flex', alignItems: 'center', height: { xs: 44, md: 24 } }}>
            <Skeleton variant="circular" animation="wave" sx={{ width: 24, height: 24 }} />
          </Grid>
          {/* Title and its intro copy */}
          <Skeleton variant="rounded" animation="wave" sx={{ width: '80%', height: '1.875rem' }} />
          <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Skeleton variant="rounded" animation="wave" sx={{ width: '100%', height: '1.25rem' }} />
            <Skeleton variant="rounded" animation="wave" sx={{ width: '60%', height: '1.25rem' }} />
          </Grid>
          {/* The form: fields on top, its two buttons at the bottom */}
          <Grid
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'space-between',
              paddingBottom: { xs: 4, md: 0 },
              gap: 2,
            }}
          >
            <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[0, 1, 2].map((field) => (
                <Grid key={field} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Skeleton variant="rounded" animation="wave" sx={{ width: '40%', height: '1.25rem' }} />
                  <Skeleton variant="rounded" animation="wave" sx={{ width: '100%', height: 44 }} />
                </Grid>
              ))}
            </Grid>
            <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Skeleton variant="rounded" animation="wave" sx={{ width: '100%', height: 52, borderRadius: 26 }} />
              <Skeleton variant="rounded" animation="wave" sx={{ width: '100%', height: 52, borderRadius: 26 }} />
            </Grid>
          </Grid>
        </Grid>
      </SafeSpacePadder>
    </div>
  );
}
